import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, TrendingDown, Wallet, Coins, Bike, Pill, Calculator } from "lucide-react";
import { api } from "../../lib/api";
import { fcfa } from "../../lib/format";

const MONTHS = [
  { v: "", l: "Toute l'année" },
  { v: "1", l: "Janvier" }, { v: "2", l: "Février" }, { v: "3", l: "Mars" }, { v: "4", l: "Avril" },
  { v: "5", l: "Mai" }, { v: "6", l: "Juin" }, { v: "7", l: "Juillet" }, { v: "8", l: "Août" },
  { v: "9", l: "Septembre" }, { v: "10", l: "Octobre" }, { v: "11", l: "Novembre" }, { v: "12", l: "Décembre" },
];

export default function Comptabilite() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api.get(`/admin/comptabilite?year=${year}${month ? `&month=${month}` : ""}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(String(y));

  const Stat = ({ icon: Icon, label, value, color, testid }) => (
    <div className="bg-white border border-brand-line rounded-2xl p-5" data-testid={testid}>
      <p className="text-xs text-brand-muted flex items-center gap-1"><Icon className={`w-3.5 h-3.5 ${color}`} /> {label}</p>
      <p className="font-heading font-black text-2xl text-brand-ink mt-1">{value}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink flex items-center gap-2"><Calculator className="w-7 h-7 text-brand" /> Comptabilité</h1>
          <p className="text-brand-muted mt-1">Vue d'ensemble de vos finances.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="compta-month" className="border border-brand-line rounded-lg px-3 py-2 text-sm">
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="compta-year" className="border border-brand-line rounded-lg px-3 py-2 text-sm">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {data === null ? <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-brand" /></div>
        : data === false ? <p className="text-sm text-red-500">Impossible de charger la comptabilité.</p>
        : (
          <>
            {/* Résultat de la période */}
            <div className="grid sm:grid-cols-3 gap-3">
              <Stat icon={TrendingUp} label="Chiffre d'affaires" value={fcfa(data.revenue)} color="text-green-600" testid="compta-revenue" />
              <Stat icon={TrendingDown} label="Dépenses" value={fcfa(data.total_expenses)} color="text-red-500" testid="compta-expenses" />
              <div className={`rounded-2xl p-5 border ${data.net >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="compta-net">
                <p className="text-xs flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Bénéfice net</p>
                <p className={`font-heading font-black text-2xl mt-1 ${data.net >= 0 ? "text-green-800" : "text-red-700"}`}>{fcfa(data.net)}</p>
              </div>
            </div>
            <p className="text-xs text-brand-muted -mt-2">{data.orders_count} commande(s) payée(s) sur la période.</p>

            {/* Sommes dues (à date) */}
            <div>
              <h2 className="font-heading font-bold text-brand-ink mb-3">Sommes à récupérer / à payer (à ce jour)</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat icon={Wallet} label="Cash à remettre (vendeurs)" value={fcfa(data.cash_to_remit)} color="text-amber-600" testid="compta-cash-remit" />
                <Stat icon={Pill} label="À encaisser (pharmacies)" value={fcfa(data.pharmacy_to_collect)} color="text-brand" testid="compta-pharmacy" />
                <Stat icon={Coins} label="Commissions à payer" value={fcfa(data.commissions_due)} color="text-amber-600" testid="compta-commissions" />
                <Stat icon={Bike} label="À payer aux livreurs" value={fcfa(data.livreurs_to_pay)} color="text-green-600" testid="compta-livreurs" />
              </div>
            </div>

            {/* Dépenses par catégorie */}
            {data.expenses_by_category.length > 0 && (
              <div className="bg-white border border-brand-line rounded-2xl p-5">
                <h2 className="font-heading font-bold text-brand-ink mb-3">Dépenses par catégorie</h2>
                {data.expenses_by_category.map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-1.5 text-sm border-b border-brand-line/50 last:border-0">
                    <span className="text-brand-sub">{c.label}</span>
                    <span className="font-semibold text-brand-ink">{fcfa(c.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-brand-cream border border-brand-line rounded-2xl p-4 text-sm text-brand-sub">
              💡 Le <b>Chiffre d'affaires</b> et les <b>Dépenses</b> concernent la période choisie. Les <b>sommes à récupérer/payer</b> sont le total actuel, quelle que soit la période. Pour le détail des dépenses, allez dans <b>Dépenses</b>.
            </div>
          </>
        )}
    </div>
  );
}
